import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Container, Row, Col, Card, Button, ButtonGroup, Badge, Modal } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, } from 'recharts';
import 'bootstrap/dist/css/bootstrap.min.css';
import OutageDataCard from './OutageDataCard';
// Supabase setup
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);


function App() {

  // Add these state variables at the top of your component
  const [showCurrentDemand, setShowCurrentDemand] = useState(true);
  const [showForecast, setShowForecast] = useState(true);

  const [activeSiteMaintenance, setActiveSiteMaintenance] = useState(true); // Set to true to test

    // Modal for site maintenance
  const MaintenanceModal = () => (
    <Modal show={activeSiteMaintenance} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title>Site Maintenance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          The dashboard is currently undergoing scheduled maintenance. Please check back soon.
        </p>
      </Modal.Body>
    </Modal>
  );
  

    // Legend click handler
  const CustomLegend = (props) => {
    const { payload } = props;
    
    const handleToggle = (dataKey) => {
      if (dataKey === 'current_demand') {
        setShowCurrentDemand(!showCurrentDemand);
      } else if (dataKey === 'next_hour_demand_forecast') {
        setShowForecast(!showForecast);
      }
    };
    
    return (
      <div className="d-flex justify-content-center gap-4 mt-2">
        {payload.map((entry, index) => {
          const isCurrentDemand = entry.dataKey === 'current_demand';
          const isActive = isCurrentDemand ? showCurrentDemand : showForecast;
          
          return (
            <div key={`legend-${index}`} className="d-flex align-items-center gap-2">
              <svg width="16" height="16" style={{ cursor: 'pointer' }} onClick={() => handleToggle(entry.dataKey)}>
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  fill={isActive ? entry.color : 'transparent'}
                  stroke={entry.color}
                  strokeWidth="2"
                />
              </svg>
              <div
                style={{
                  width: '20px',
                  height: '3px',
                  backgroundColor: entry.color,
                  borderStyle: entry.payload.strokeDasharray ? 'dashed' : 'solid',
                  borderWidth: entry.payload.strokeDasharray ? '1px 0' : '0',
                  borderColor: entry.color,
                  opacity: isActive ? 1 : 0.3
                }}
              />
              <span style={{ color: isActive ? '#333' : '#999' }}>
                {entry.value}
              </span>
            </div>
          );
        })}
      </div>
    );
  };


  const [gridData, setGridData] = useState([]);
  const [latestData, setLatestData] = useState({});
  const [outageData, setOutageData] = useState([]);
  const [timeframe, setTimeframe] = useState('1w'); // Default to 24 hours
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastErrors, setForecastErrors] = useState([]);
  const [aggregatedErrors, setAggregatedErrors] = useState({
    averageError: 0,
    averagePercentError: 0,
    maxError: 0,
    minError: 0,
    totalSamples: 0
  });

  useEffect(() => {
    fetchData();
  }, [timeframe]);

  // Fetch data from Supabase based on selected timeframe
  const fetchData = async () => {
    setLoading(true);
    
    let timeConstraint;
    const now = new Date();
    
    switch(timeframe) {
      case '48h':
        timeConstraint = new Date(now.getTime() - 48 * 60 * 60 * 1000).toISOString();
        break;
      case '72h':
        timeConstraint = new Date(now.getTime() - 72 * 60 * 60 * 1000).toISOString();
        break;
      case '1w':
        timeConstraint = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default: // 24h
        timeConstraint = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    }
    
    try {
      let query = supabase
        .from('luma_scrape_results')
        .select('*')
        .order('timestamp', { ascending: true });
        
      if (timeConstraint) {
        query = query.gte('timestamp', timeConstraint);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Format dates and properly process numeric values
        const formattedData = data.map(item => {
          // Ensure timestamp is a Date object for visualization
          const timestamp = new Date(item.timestamp);
          
          // Ensure all numeric fields are actual numbers, not strings
          // This is crucial for the charts to render properly
          return {
            ...item,
            timestamp: timestamp,
            timestampMs: timestamp.getTime(), // Add a millisecond timestamp for charts 
            formattedTimestamp: timestamp.toLocaleString(),
            current_demand: Number(item.current_demand),
            current_demand_max: Number(item.current_demand_max),
            next_hour_demand_forecast: Number(item.next_hour_demand_forecast),
            next_hour_demand_forecast_max: Number(item.next_hour_demand_forecast_max),
            current_reserve: Number(item.current_reserve),
            current_reserve_max: Number(item.current_reserve_max),
            peak_demand_forecast: Number(item.peak_demand_forecast),
            peak_reserve_forecast: Number(item.peak_reserve_forecast)
          };
        });
        
        console.log("Processed data sample:", formattedData[0]); // Debug output
        
        setGridData(formattedData);
        setLatestData(formattedData[formattedData.length - 1]);
        
        // Calculate forecast errors (both itemized and aggregated)
        calculateErrors(formattedData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  // Fetch outage data from Supabase
  useEffect(() => {
    const fetchOutageData = async () => {
      try {
        const { data, error } = await supabase
          .from('outage_snapshot')
          .select('*')
          .order('timestamp', { ascending: true });

        if (error) {
          throw error;
        }
        console.log("Outage data sample:", data); // Debug output
        setOutageData(data || []);
      } catch (err) {
        console.error('Error fetching outage data:', err);
      }
    };

    fetchOutageData();
  }, [timeframe]);
  
  // Calculate the error between current demand and forecasted demand
  const calculateErrors = (data) => {
    // Find pairs of values where we can compare actual demand with the forecast from previous hour
    const errors = [];
    
    // First collect errors between current demand and next hour forecast at the same timestamp
    const sameTimestampErrors = [];
    
    // For current timestamps, collect errors between forecast and actual demand
    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      
      if (current.current_demand !== null && current.next_hour_demand_forecast !== null) {
        const absoluteError = Math.abs(current.next_hour_demand_forecast - current.current_demand);
        const percentageError = ((absoluteError / current.current_demand) * 100).toFixed(2);
        
        sameTimestampErrors.push({
          timestamp: current.formattedTimestamp,
          forecast: current.next_hour_demand_forecast,
          actual: current.current_demand,
          absoluteError,
          percentageError: Number(percentageError)
        });
      }
    }
    
    // For sequential timestamps (~1 hour apart), collect errors
    for (let i = 1; i < data.length; i++) {
      // Check if timestamps are approximately 1 hour apart (with some tolerance)
      const timeDiff = (data[i].timestamp - data[i-1].timestamp) / (1000 * 60 * 60);
      
      if (Math.abs(timeDiff - 1) < 0.1) { // If timestamps are ~1 hour apart
        console.log("Shouldnt go in here");
        const forecast = data[i-1].next_hour_demand_forecast;
        const actual = data[i].current_demand;
        
        if (forecast !== null && actual !== null) {
          const absoluteError = Math.abs(forecast - actual);
          const percentageError = ((absoluteError / actual) * 100).toFixed(2);
          
          errors.push({
            timestamp: data[i].formattedTimestamp,
            forecast,
            actual,
            absoluteError,
            percentageError: Number(percentageError)
          });
        }
      }
    }
    
    // Calculate aggregated error statistics
    if (sameTimestampErrors.length > 0) {
      const totalAbsoluteError = sameTimestampErrors.reduce((sum, item) => sum + item.absoluteError, 0);
      const totalPercentageError = sameTimestampErrors.reduce((sum, item) => sum + Number(item.percentageError), 0);
      const maxError = Math.max(...sameTimestampErrors.map(item => Number(item.percentageError)));
      const minError = Math.min(...sameTimestampErrors.map(item => Number(item.percentageError)));
      
      setAggregatedErrors({
        averageError: (totalAbsoluteError / sameTimestampErrors.length).toFixed(2),
        averagePercentError: (totalPercentageError / sameTimestampErrors.length).toFixed(2),
        maxError: maxError.toFixed(2),
        minError: minError.toFixed(2),
        totalSamples: sameTimestampErrors.length
      });
    }
    
    setForecastErrors(errors);
  };

  // Format the tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip bg-light p-3 border rounded shadow-sm">
          <p className="mb-0"><strong>Time:</strong> {new Date(label).toLocaleString()}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              <strong>{entry.name}:</strong> {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Helper function to get color based on ratio
  const getRatioColor = (ratio) => {
    // Convert ratio to a 0-1 scale where 0 is green and 1 is red
    const normalizedRatio = Math.min(Math.max(ratio, 0), 1);
    
    // Calculate RGB values for gradient from green to yellow to red
    const r = normalizedRatio <= 0.5 ? Math.round(510 * normalizedRatio) : 255;
    const g = normalizedRatio <= 0.5 ? 255 : Math.round(510 * (1 - normalizedRatio));
    const b = 0;
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Render Pie Chart for ratio visualization
  const RatioPieChart = ({ value, max, title }) => {
    // Calculate ratio (prevent division by zero)
    const ratio = max > 0 ? value / max : 0;
    const percentage = Math.round(ratio * 100);
    
    // Data for pie chart
    const data = [
      { name: 'Used', value: percentage },
      { name: 'Available', value: 100 - percentage }
    ];
    
    const pieColor = getRatioColor(ratio);
    
    return (
      <div style={{ height: 120 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={25}
              outerRadius={40}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              labelLine={false}
            >
              <Cell key="used" fill={pieColor} />
              <Cell key="available" fill="#e0e0e0" />
            </Pie>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="bold">
              {percentage}%
            </text>
          </PieChart>
        </ResponsiveContainer>
        <div className="text-center" style={{ fontSize: '0.85rem', marginTop: '-4px' }}>
          <small className="text-muted">{title}</small>
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading && gridData.length === 0) {
    return (
      <Container className="my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h4 className="mt-3">Loading grid data...</h4>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container className="my-5">
        <Card className="border-danger">
          <Card.Header className="bg-danger text-white">Error</Card.Header>
          <Card.Body>
            <Card.Title>Failed to load grid data</Card.Title>
            <Card.Text>{error}</Card.Text>
            <Button variant="primary" onClick={fetchData}>Retry</Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h1 className="text-center mb-4"> Puerto Rico Grid Monitoring Dashboard</h1>
      {/* Maintenance Modal - only show if activeSiteMaintenance is true */}
      {activeSiteMaintenance ? <MaintenanceModal /> : null}

      
      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-3 p-3 bg-light border rounded">
          <strong>Debug:</strong> Loaded {gridData.length} records
        </div>
      )}
      {/* Timeframe Selection */}
      <Row className="mb-4 justify-content-center">
        <Col xs={12} md={8} lg={6} className="text-center">
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Select Timeframe</Card.Title>
              <ButtonGroup className="w-100">
                <Button 
                  variant={timeframe === '24h' ? 'primary' : 'outline-primary'} 
                  onClick={() => setTimeframe('24h')}
                >
                  24 hrs
                </Button>
                <Button 
                  variant={timeframe === '48h' ? 'primary' : 'outline-primary'} 
                  onClick={() => setTimeframe('48h')}
                >
                  48 hrs
                </Button>
                <Button 
                  variant={timeframe === '72h' ? 'primary' : 'outline-primary'} 
                  onClick={() => setTimeframe('72h')}
                >
                  72 hrs
                </Button>
                <Button 
                  variant={timeframe === '1w' ? 'primary' : 'outline-primary'} 
                  onClick={() => setTimeframe('1w')}
                >
                  1 week
                </Button>
              </ButtonGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Outage Data Card */}
      {/* <OutageDataCard outageData={outageData} />      */}

      {/* Current Grid Scorecard */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow border-0">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Current Grid Scorecard</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Card.Title>Current Demand</Card.Title>
                          <h3>{latestData.current_demand?.toLocaleString() || 'N/A'}</h3>
                          <Card.Text className="text-muted">
                            Max: {latestData.current_demand_max?.toLocaleString() || 'N/A'}
                          </Card.Text>
                        </div>
                        <div className="ms-2" style={{ width: '120px' }}>
                          {latestData.current_demand && latestData.current_demand_max && (
                            <RatioPieChart 
                              value={latestData.current_demand} 
                              max={latestData.current_demand_max} 
                              title="% of Max"
                            />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Card.Title>Next Hour Forecast</Card.Title>
                          <h3>{latestData.next_hour_demand_forecast?.toLocaleString() || 'N/A'}</h3>
                          <Card.Text className="text-muted">
                            Max: {latestData.next_hour_demand_forecast_max?.toLocaleString() || 'N/A'}
                          </Card.Text>
                        </div>
                        <div className="ms-2" style={{ width: '120px' }}>
                          {latestData.next_hour_demand_forecast && latestData.next_hour_demand_forecast_max && (
                            <RatioPieChart 
                              value={latestData.next_hour_demand_forecast} 
                              max={latestData.next_hour_demand_forecast_max} 
                              title="% of Max"
                            />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>                
                <Col xs={12} md={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Card.Title>Current Reserve</Card.Title>
                          <h3>{latestData.current_reserve?.toLocaleString() || 'N/A'}</h3>
                          <Card.Text className="text-muted">
                            Max: {latestData.current_reserve_max?.toLocaleString() || 'N/A'}
                          </Card.Text>
                        </div>
                        <div className="ms-2" style={{ width: '120px' }}>
                          {latestData.current_reserve && latestData.current_reserve_max && (
                            <RatioPieChart 
                              value={latestData.current_reserve} 
                              max={latestData.current_reserve_max} 
                              title="% of Max"
                            />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12} md={6} className="mb-3 mb-md-0">
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Card.Title>Peak Demand Forecast</Card.Title>
                          <h3>{latestData.peak_demand_forecast?.toLocaleString() || 'N/A'}</h3>
                        </div>
                        <div className="ms-2" style={{ width: '120px' }}>
                          {latestData.peak_demand_forecast && latestData.current_demand_max && (
                            <RatioPieChart 
                              value={latestData.peak_demand_forecast} 
                              max={latestData.current_demand_max} 
                              title="% of System Max"
                            />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Card.Title>Peak Reserve Forecast</Card.Title>
                          <h3>{latestData.peak_reserve_forecast?.toLocaleString() || 'N/A'}</h3>
                        </div>
                        <div className="ms-2" style={{ width: '120px' }}>
                          {latestData.peak_reserve_forecast && latestData.current_reserve_max && (
                            <RatioPieChart 
                              value={latestData.peak_reserve_forecast} 
                              max={latestData.current_reserve_max} 
                              title="% of Reserve Max"
                            />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Charts Section */}
      <Row className="mb-4">
        {/* Special Case: Current Demand + Next Hour Forecast Chart */}
        <Col xs={12} className="mb-4">
          <Card className="shadow border-0">
  <Card.Header className="bg-info text-white">
    <h5 className="mb-0">Current Demand vs. Next Hour Forecast</h5>
  </Card.Header>
  <Card.Body>
    <div style={{ width: '100%', height: 400 }}>
      {gridData.length > 0 ? (
        <ResponsiveContainer>
          <LineChart
            data={gridData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestampMs" 
              tickFormatter={(timestamp) => new Date(timestamp).toLocaleTimeString()} 
              type="number"
              domain={['auto', 'auto']}
              scale="time"
              name="Time"
            />
            <YAxis 
              name="Demand"
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
            <Line 
              type="monotone" 
              dataKey="current_demand" 
              name="Current Demand"
              stroke="#8884d8" 
              activeDot={{ r: 8 }} 
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              opacity={showCurrentDemand ? 1 : 0.1}
              strokeOpacity={showCurrentDemand ? 1 : 0.1}
            />
            <Line 
              type="monotone" 
              dataKey="next_hour_demand_forecast" 
              name="Next Hour Forecast"
              stroke="#82ca9d" 
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              opacity={showForecast ? 1 : 0.1}
              strokeOpacity={showForecast ? 1 : 0.1}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
                  <div className="text-center p-5">
                    <p>No data available for the selected timeframe</p>
                  </div>
                )}
              </div>
              
              {/* Forecast Error Analysis - Enhanced version */}
              <div className="mt-4">
                <h5>Forecast Error Analysis</h5>
                
                {/* New: Aggregated Error Summary */}
                <Row className="mb-3">
                  <Col>
                    <Card className="bg-light">
                      <Card.Body>
                        <h6>Error Summary across {aggregatedErrors.totalSamples} samples</h6>
                        <Row>
                          <Col xs={6} md={3} className="border-end">
                            <div className="text-center">
                              <div className="fs-5 fw-bold">{aggregatedErrors.averageError}</div>
                              <div className="text-muted small">Avg Absolute Error</div>
                            </div>
                          </Col>                          
                          <Col xs={6} md={3} className="border-end">
                            <div className="text-center text-warning">
                              <div className="fs-5 fw-bold">{aggregatedErrors.averagePercentError}%</div>
                              <div className="text-muted small">Avg Percentage Error</div>
                            </div>
                          </Col>
                          <Col xs={6} md={3} className="border-end">
                            <div className="text-center">
                              <div className="fs-5 fw-bold text-danger">{aggregatedErrors.maxError}%</div>
                              <div className="text-muted small">Max Error</div>
                            </div>
                          </Col>
                          <Col xs={6} md={3}>
                            <div className="text-center">
                              <div className="fs-5 fw-bold text-success">{aggregatedErrors.minError}%</div>
                              <div className="text-muted small">Min Error</div>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                {/* Original Error Table - showing most recent 5 errors */}
                {forecastErrors.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead className="table-light">
                        <tr>
                          <th>Timestamp</th>
                          <th>Forecasted</th>
                          <th>Actual</th>
                          <th>Absolute Error</th>
                          <th>Error %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecastErrors.slice(-5).map((error, index) => (
                          <tr key={index}>
                            <td>{error.timestamp}</td>
                            <td>{error.forecast.toLocaleString()}</td>
                            <td>{error.actual.toLocaleString()}</td>
                            <td>{error.absoluteError.toLocaleString()}</td>
                            <td>
                              <Badge bg={error.percentageError > 10 ? 'danger' : error.percentageError > 5 ? 'warning' : 'success'}>
                                {error.percentageError}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No comparable forecast-actual pairs found in this timeframe.</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>   
      {/* Individual Metric Charts */}
      <Row>
        <Col xs={12} lg={6} className="mb-4">
          <Card className="shadow border-0 h-100">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">Current Reserve</h5>
            </Card.Header>
            <Card.Body>
              <div style={{ width: '100%', height: 300 }}>
                {gridData.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart
                      data={gridData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestampMs" 
                        tickFormatter={(timestamp) => new Date(timestamp).toLocaleTimeString()} 
                        type="number"
                        domain={['auto', 'auto']}
                        scale="time"
                      />
                      <YAxis domain={['auto', 'auto']} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="current_reserve" 
                        name="Current Reserve"
                        stroke="#28a745" 
                        activeDot={{ r: 8 }} 
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="current_reserve_max" 
                        name="Max Reserve"
                        stroke="#20c997" 
                        strokeDasharray="5 5"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center p-5">
                    <p>No data available for the selected timeframe</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={12} lg={6} className="mb-4">
          <Card className="shadow border-0 h-100">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">Peak Forecasts</h5>
            </Card.Header>
            <Card.Body>
              <div style={{ width: '100%', height: 300 }}>
                {gridData.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart
                      data={gridData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestampMs" 
                        tickFormatter={(timestamp) => new Date(timestamp).toLocaleTimeString()} 
                        type="number"
                        domain={['auto', 'auto']}
                        scale="time"
                      />
                      <YAxis domain={['auto', 'auto']} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="peak_demand_forecast" 
                        name="Peak Demand Forecast"
                        stroke="#fd7e14" 
                        activeDot={{ r: 8 }}
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="peak_reserve_forecast" 
                        name="Peak Reserve Forecast"
                        stroke="#ffc107" 
                        activeDot={{ r: 8 }}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center p-5">
                    <p>No data available for the selected timeframe</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Last Updated Timestamp */}
      <Row className="mt-4">
        <Col className="text-center">
          <Card className="shadow-sm">
            <Card.Body>
              <p className="mb-0">
                <strong>Updated last at:</strong> {latestData.formattedTimestamp || 'N/A'}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Footer */}
      <Row className="mt-5">
        <Col className="text-center text-muted">
          <p>Grid Monitoring Dashboard &copy; 2025</p>
        </Col>
      </Row>
    </Container>
  );
}

export default App;


function variance(arr) {
  const mean = arr.reduce((sum, val) => sum + val, 0) / arr.length;
  const squaredDiffs = arr.map(val => Math.pow(val - mean, 2));
  return squaredDiffs.reduce((sum, val) => sum + val, 0) / arr.length;
}

const calculateErrors= (actual,forecast) => {
  
  // Calculate MSE
  var summedSquaredError = 0;
  for (let i = 0; i < actual.length; i++) {
    const actualValue = actual[i].value;
    const forecastValue = forecast[i].value;
    const error = actualValue - forecastValue;
    summedSquaredError += error * error;
  }
  const mse = summedSquaredError / actual.length;
  // normalized mse is calculated by diving mse by the variance of the actual values
  // print type of actual

  const varianceOfActualValues = variance(actual.map(d => d.value));
  const normalizedMSE = mse / varianceOfActualValues;
  const rmse = Math.sqrt(mse);
  const mpe = (summedSquaredError / actual.length) / actual.reduce((acc, val) => acc + val.value, 0) * 100;
  const errorValues = {
    NMSE:normalizedMSE,
    MSE: mse,
    RMSE: rmse,
    MPE: mpe
  }
  return errorValues;
}
